/**
 * テーブルアラート処理のComposable
 */

import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { AlertDataObject } from '@/coreDomainsModule/alert';
import { InventoryAlertKinds } from '../domains/entities/Alert';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { assertNever } from '@/coreDomainsModule/assertNever';
import { useTableCalculation } from './useTableCalculation';
import { i18n } from '@/i18n';
import type { IMonthlyBusinessData } from '../domains/entities/MonthlyData';

/**
 * アラートデータの型定義
 */
export interface AlertData {
  /** ビジネス情報 */
  business: IBusiness;
  /** 月の差分 */
  monthDiff: number;
  /** アラートの種類 */
  kind?: InventoryAlertKinds;
}

/**
 * アラートチェック結果の型定義
 */
export interface AlertCheckResult {
  /** ビジネス情報 */
  business: IBusiness;
  /** 月の差分 */
  monthDiff: number;
  /** アラートの種類リスト */
  alertKinds: InventoryAlertKinds[];
}

/**
 * テーブルアラート機能を提供するComposable
 * @description
 * Store内のアラート処理ロジックを独立したComposableとして分離。
 * diContainerから必要なリポジトリを直接取得して使用する。
 */
export const useTableAlert = () => {
  // i18n
  const t = i18n.t;

  // テーブル計算Composableを使用
  const { getMonthlyData } = useTableCalculation();

  /**
   * テーブルアラートデータを取得する
   * 元の useInventoryStore の getTableAlertData 関数と同等の処理
   * @param monthlyDataCache - 月次データキャッシュ
   * @param business - ビジネス情報
   * @param monthDiff - 月の差分
   * @param kind - フィルタしたいアラートのタイプ（オプション）
   * @returns アラートデータオブジェクトの配列
   */
  const getTableAlertData = (
    monthlyDataCache: Record<string, IMonthlyBusinessData>,
    business: IBusiness,
    monthDiff: number,
    kind?: InventoryAlertKinds
  ): AlertDataObject[] => {
    // 予期しないステータスをチェック
    const alertStatusList: InventoryAlertKinds[] = checkUnexpectedStatus(monthlyDataCache, business, monthDiff);
    const res: AlertDataObject[] = [];

    // 現在日時から対象月を計算
    const nowDate = DateService.convertDateToIYearMonth(new Date());
    const targetDateBuf = DateService.updateYearMonth(nowDate.year, nowDate.month, monthDiff);
    const targetDate = {
      year: targetDateBuf.updateYear,
      month: targetDateBuf.updateMonth,
    };

    // アラート用の発注数量を取得
    const orderAmount = getOrderAmountForAlert(business, monthDiff);

    // 対象月の月次データを取得
    const targetMonthData = getMonthlyData(monthlyDataCache, business.businessId, monthDiff);

    // 各アラートステータスに対する処理
    alertStatusList.map((alertStatus) => {
      switch (alertStatus) {
        case InventoryAlertKinds.ArrivalGreaterThanOrder:
          // 入荷 > 発注 の違算アラート
          if (!kind || kind === alertStatus) {
            res.push({
              data: {
                kind: alertStatus,
                type: 'error',
                title: t('違算'),
                subtitle: t('(入荷 > 発注)'),
                description: t('システムで発注が遅れている可能性があります。または納期変更できていない発注があります。'),
                showConfirmButton: false,
              },
              additionalData: {
                yearMonth: targetDate,
                arrivalAmount: monthDiff < 0 ? targetMonthData.arrival : targetMonthData.exceptedArrivalForNormalMode,
                orderAmount: orderAmount,
              },
            });
          }
          break;

        case InventoryAlertKinds.OrderGreaterThanArrival:
          // 入荷 < 発注 の違算アラート
          if (!kind || kind === alertStatus) {
            res.push({
              data: {
                kind: alertStatus,
                type: 'warning',
                title: t('違算'),
                subtitle: t('(入荷 < 発注)'),
                description: t('仕入れ先へ発注が漏れている可能性があります。または納期変更できていない発注があります。'),
                showConfirmButton: false,
              },
              additionalData: {
                yearMonth: targetDate,
                arrivalAmount: monthDiff < 0 ? targetMonthData.arrival : targetMonthData.exceptedArrivalForNormalMode,
                orderAmount: orderAmount,
              },
            });
          }
          break;

        case InventoryAlertKinds.StorageMoved:
          // 置場移動アラート
          if (!kind || kind === alertStatus) {
            res.push({
              data: {
                kind: alertStatus,
                type: 'warning',
                title: t('置場移動の可能性があります'),
                description: `${t('置き場が移動された可能性があります。')}\n${t('集計を確認し、移動完了日を登録してください。')}`,
                showConfirmButton: true,
              },
            });
          }
          break;

        default:
          assertNever(alertStatus);
      }
    });

    return res;
  };

  /**
   * 予期しないステータスをチェックする
   * 元の useInventoryStore の _checkUnexpectedStatus 関数と同等の処理
   * @param monthlyDataCache - 月次データキャッシュ
   * @param business - ビジネス情報
   * @param monthDiff - 月の差分
   * @returns 検出されたアラートの種類の配列
   */
  const checkUnexpectedStatus = (
    monthlyDataCache: Record<string, IMonthlyBusinessData>,
    business: IBusiness,
    monthDiff: number
  ): InventoryAlertKinds[] => {
    const res: InventoryAlertKinds[] = [];

    // 当月以降の場合は処理しない
    if (!(monthDiff < 0)) return res;

    // 発注数量の合計を取得
    const orderAmountSum = getOrderAmountForAlert(business, monthDiff);

    // 対象月と前月の月次データを取得
    const targetMonthData = getMonthlyData(monthlyDataCache, business.businessId, monthDiff);
    const targetBeforeMonthData = getMonthlyData(monthlyDataCache, business.businessId, monthDiff - 1);

    // 各種数量データを取得
    const arrivalAmount = targetMonthData.arrival; // 対象月入荷数
    const inventoryAmount = targetMonthData.endInventory; // 対象月末在庫
    const beforeInventoryAmount = targetBeforeMonthData.endInventory; // 対象前月末在庫
    const salesResultAmount = targetMonthData.salesResult; // 対象月販売実績

    // 違算チェック（入荷 > 発注）
    if (arrivalAmount > orderAmountSum) {
      res.push(InventoryAlertKinds.ArrivalGreaterThanOrder);
    }
    // 違算チェック（入荷 < 発注）
    else if (arrivalAmount < orderAmountSum) {
      res.push(InventoryAlertKinds.OrderGreaterThanArrival);
    }

    // 置場移動チェック
    if (beforeInventoryAmount && inventoryAmount !== beforeInventoryAmount + arrivalAmount - salesResultAmount) {
      res.push(InventoryAlertKinds.StorageMoved);
    }

    return res;
  };

  /**
   * アラートに必要な発注数量を計算する
   * 元の useInventoryStore の _getOrderAmountForAlert 関数と同等の処理
   * @param business - ビジネス情報
   * @param monthDiff - 月の差分
   * @returns 発注数量の合計
   */
  const getOrderAmountForAlert = (business: IBusiness, monthDiff: number): number => {
    // 現在日時から対象月を計算
    const nowDate = DateService.convertDateToIYearMonth(new Date());
    const targetDate = DateService.updateYearMonth(nowDate.year, nowDate.month, monthDiff);

    // 対象月の発注データをフィルタリング
    const orderData = business.recommend.filter(
      (res) =>
        res.crossDate?.year === targetDate.updateYear &&
        res.crossDate?.month === targetDate.updateMonth &&
        (res.status === 'DisplayEnded' || res.status === 'Delivered')
    );

    // 発注数量の合計を計算
    return orderData.reduce((res, orderDetum) => (res += orderDetum.amount), 0);
  };

  // 公開するAPIを返す
  return {
    getTableAlertData,
    checkUnexpectedStatus,
    getOrderAmountForAlert,
  };
};

// 型定義は上記のinterfaceで既にexportされているため、重複するexport文は不要
